import 'project/polyfill'
import { useState } from 'react'
import classNames from 'classnames'
import { useRouter } from 'next/router'

import {
  heroGap,
  marginHeader,
  sectionGap,
  sectionGapSmall,
  sectionPadding,
  sectionPaddingLarge,
} from 'project/padding'
import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import MailPortalHighlights, {
  virtualOfficeHighlights,
} from 'components/cards/new/common/MailPortalHighlights'
import Icon from 'components/Icon'
import Supademo from 'components/Supademo'
import { createClient } from '../../prismicio'
import { PlanSlug } from 'common/planSlugs'
import { Constants, Format, Utils } from 'common/utils'
import { getFullAddress } from 'common/utils/constants'
import WhyChoose from 'components/WhyChoose'
import FAQList from 'components/util/FAQList'
import { LocalBusinessJsonLd } from 'next-seo'
import { HoxtonMixLogo } from 'components/icons'
import { getPlanPrice } from 'common/utils/planPrice'
import HeroBanner from 'components/hero/HeroBanner'
import { getAddons, getPlans } from 'common/planData'
import HeadingAndText from 'components/HeadingAndText'
import { getFAQList, getReviews } from 'common/cms-sdk'
import { seoStrings } from 'common/strings/seo-strings'
import CompareCards from 'components/cards/CompareCards'
import useWindowSize from 'components/util/useWindowSize'
import { I18NConfig } from 'next/dist/server/config-shared'
import ReviewsSection from 'components/sections/ReviewsSection'
import { NextPageWithLayout } from '../../types/nextPageWithLayout'
import SubscriptionPricing from 'components/SubscriptionPricing'
import { ButtonType } from '../../types/global'
import SignUpIncludes from 'components/cards/new/SignUpIncludes'
import KeyBenefitsCard from 'components/cards/new/KeyBenefitsCard'
import KeyBenefitsSection from 'components/sections/KeyBenefitsSection'
import SalesPitchCard from 'components/cards/new/common/SalesPitchCard'
import PrivacyServiceCard from 'components/cards/new/PrivacyServiceCard'
import { TAddon, TFAQList, TPlanSummary, TReviews } from 'common/cms-types'
import { virtualOfficeStrings } from 'common/strings/virtual-office-strings'
import EmbededNewsletterForm from 'components/sections/EmbededNewsletterForm'
import PostroomReinventedCard from 'components/cards/new/PostroomReinventedCard'
import { auHomeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import PageContainer from 'components/util/containers/PageContainer'
import { getTenantFromPath, Tenant, tenantToSlug } from 'common/tenant'

export type PlansPageType = {
  plans: TPlanSummary[]
  reviews: TReviews | null
  faqList: TFAQList
  addons: TAddon[]
}

const VirtualOfficeSydneyPage: NextPageWithLayout<PlansPageType> = ({
  addons,
  faqList,
  plans,
  reviews,
}) => {
  const router = useRouter()
  const size = useWindowSize()

  const tenantSlug = tenantToSlug(Tenant.AU)
  // dailyOnYearlyPlan returns cents
  const dailyPrice = Format.money(
    getPlanPrice(tenantSlug).dailyOnYearlyPlan(PlanSlug.SCAN_LITE),
    tenantSlug,
  )
  const seoProps: SeoContainerProps['seoProps'] = {
    description: `Get a prestigious Sydney CBD virtual office address with mail scanning and AI-powered management. Professional business address from ${dailyPrice} per day.`,
    openGraph: {
      description: `Professional Sydney virtual office with mail scanning. CBD business address from ${dailyPrice} per day. Register your Australian company address today.`,
      url: 'https://www.hoxtonmix.com/au/virtual-office-sydney/',
    },
    title: `Virtual Office Sydney - CBD Business Address from ${dailyPrice}/day`,
  }

  // Handle cases where data might not be loaded
  if (!plans || !faqList) {
    return null
  }

  return (
    <SeoContainer
      breadcrumb={[
        ...auHomeBreadcrumb(),
        {
          item: 'https://www.hoxtonmix.com/au/virtual-office-sydney/',
          name: 'Virtual Office Sydney',
          position: 2,
        },
      ]}
      seoProps={seoProps}
      tenant={Tenant.AU}
    >
      <LocalBusinessJsonLd
        type={'LocalBusiness'}
        id={'https://www.hoxtonmix.com/au/virtual-office-sydney/#localbusiness'}
        name={Constants.companyInformation.name}
        description={
          'Professional Sydney CBD virtual office with mail scanning and AI-powered management'
        }
        images={['https://www.hoxtonmix.com/organisation.png']}
        openingHours={{
          closes: '18:00',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
        }}
        geo={{
          latitude: '-33.869355105507786',
          longitude: '151.20675791534038',
        }}
        hasMap={`https://www.google.com/maps/search/${encodeURIComponent(
          getFullAddress(Tenant.AU),
        )}`}
        telephone={Constants.companyInformation.phoneAu}
        url='https://www.hoxtonmix.com/au/virtual-office-sydney/'
        address={{
          addressCountry: 'AU',
          addressLocality: 'Sydney',
          addressRegion: 'NSW',
          postalCode: '2000',
          streetAddress: Constants.companyInformation.address2Au,
        }}
      />
      <main
        className={classNames(
          'overflow-x-hidden new-design d-flex flex-column',
          sectionGap,
        )}
      >
        <div className={classNames('d-flex flex-column', heroGap)}>
          <HeroBanner
            logo={<HoxtonMixLogo className='mb-5 mb-lg-8' />}
            robotImage={{
              alt: 'HaiRo™ robot mascot with Sydney mail',
              dimensions: {
                height: 1000,
                width: 827,
              },
              src: '/images/new/au/robot-holding-mail.png',
            }}
            cta={
              <SubscriptionPricing
                buttonType={ButtonType.VIRTUAL_OFFICE_SYDNEY_PAGE_HERO}
                buttonAlign='start'
                hideRegister
                hideAddress
                planSummary={plans.find((v) => v.slug === PlanSlug.SCAN_LITE)!}
                tenant={Tenant.AU}
              />
            }
            title='Virtual Office Sydney'
            description={
              <div
                className={classNames('d-flex flex-column', sectionGapSmall)}
              >
                <h3 className='fs-fluid-24-32 fw-semibold'>
                  Register your business to our address and let us handle your
                  mail! We organise, digitise and deliver your mail into a
                  secure inbox you can access from anywhere.
                </h3>
                <p className='fs-fluid-18-22 fw-semibold mb-0'>
                  Send your mail to us at
                  <br />
                  <span className='color-pink2'>
                    {`${Constants.companyInformation.address2Au} ${Constants.companyInformation.cityAu}, ${Constants.companyInformation.postal_codeAu}`}
                  </span>
                </p>
                <p className='fs-fluid-18-22 fw-semibold mb-0'>
                  And HaiRo - our AI agent will get the mail to you - wherever
                  you are!
                </p>
              </div>
            }
            subtitle={<span className='fs-fluid-24-48'></span>}
          />
          <div
            className={classNames('container d-flex flex-column', sectionGap)}
          >
            <div>
              <div
                className={classNames(
                  'd-flex flex-column',
                  marginHeader,
                  sectionPadding,
                )}
              >
                <h2
                  className={classNames(
                    'fs-fluid-36-80 lh-105 fw-semibold text-center',
                  )}
                >
                  Get a CBD business address with
                  <span className='color-pink2'> everything included </span>
                  in one simple plan.
                </h2>
              </div>

              <SignUpIncludes tenant={Tenant.AU} />
            </div>
            <div className={`d-none d-lg-flex flex-column ${sectionGapSmall}`}>
              <div className={classNames('d-flex flex-column', marginHeader)}>
                <div className={sectionPaddingLarge}>
                  <h2
                    id='demo'
                    className={classNames(
                      'fs-fluid-36-80 lh-105 fw-semibold text-center',
                    )}
                  >
                    Watch how
                    <span className='color-pink2'>
                      {' '}
                      our Sydney virtual office{' '}
                    </span>
                    works.
                  </h2>
                  <h3 className='fw-semibold text-muted mt-lg-3 mt-2 text-center fs-fluid-20-36'>
                    See how easy it is to manage your business mail from
                    anywhere in Australia.
                  </h3>
                </div>
              </div>
              <div className={classNames('px-lg-0')}>
                <Supademo showcaseId={'cmbrtzben02ml1q0iqll8zeff'} />
              </div>
            </div>
          </div>
        </div>
        <div className={classNames('container', sectionGap)}>
          <PostroomReinventedCard />
        </div>
        <div className='container'>
          <WhyChoose tenant={Tenant.AU} />
        </div>
        <div className={classNames('bg-grey6')}>
          <div className={classNames('container')}>
            <MailPortalHighlights
              title={
                <>
                  Why Australian businesses choose{' '}
                  <span className='color-pink2'>Hoxton Mix</span>
                </>
              }
            >
              <ul style={{ maxWidth: 776 }} className='list-unstyled'>
                {virtualOfficeHighlights.map((highlight, index) => (
                  <li key={index} className='d-flex mb-6'>
                    <Icon
                      width={20}
                      name='tick-circle'
                      className='me-2 flex-shrink-0 mt-2'
                    />
                    <div>
                      <span className='fw-semibold'>{highlight.title}</span> -{' '}
                      {highlight.content}
                    </div>
                  </li>
                ))}
              </ul>
            </MailPortalHighlights>
          </div>
        </div>

        <div
          className={classNames(
            'container d-flex flex-column',
            sectionGapSmall,
            sectionGap,
          )}
        >
          <div>
            <HeadingAndText
              className={classNames(marginHeader, sectionPadding)}
              classesForTitle='fs-fluid-36-80 lh-105'
              subtitleSize='large'
              title={`Key benefits for
                <span class="color-pink2">Australian businesses</span>`}
              subtitle={`Everything you need to establish and maintain your professional presence.`}
            />
            <KeyBenefitsSection tenant={Tenant.AU} />
          </div>
          {/* Privacy service not available in AU - no addons */}
          <KeyBenefitsCard tenant={Tenant.AU} />
        </div>

        {/*COMPARE PLANS*/}
        <div className={classNames('container', sectionGap)}>
          <HeadingAndText
            className={classNames(sectionPadding)}
            classesForTitle='fs-fluid-36-80 lh-105'
            title={`Compare our <span class="color-pink2">virtual office</span> plans`}
            subtitle={`Plans include a prestigious Sydney CBD address`}
          />
          <CompareCards tenant={Tenant.AU} />
        </div>
        <div className='container'>
          <SalesPitchCard
            pitchText={'Ready to get your virtual office?'}
            cta={
              <SubscriptionPricing
                hideTitle
                planSummary={plans.find((v) => v.slug === PlanSlug.SCAN_LITE)!}
                buttonType={ButtonType.VIRTUAL_OFFICE_SYDNEY_CTA}
                tenant={Tenant.AU}
              />
            }
          />
        </div>
        <div className='container'>
          <div id='faqs'>
            <FAQList
              slices={faqList?.data?.slices}
              title={faqList?.data?.title}
              subtitle={faqList?.data?.subtitle}
            />
          </div>
        </div>
        <div className={classNames('container d-flex flex-column', sectionGap)}>
          {reviews && (
            <ReviewsSection
              data={reviews}
              pageUrl='/au/virtual-office-sydney'
              tenant={Tenant.AU}
            />
          )}
          <EmbededNewsletterForm />
        </div>
      </main>
    </SeoContainer>
  )
}

export default VirtualOfficeSydneyPage

VirtualOfficeSydneyPage.getLayout = (page: React.ReactNode) => (
  <PageContainer hideFooterCard tenant={Tenant.AU} transparentHeader>
    {page}
  </PageContainer>
)

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient()
    const tenant = getTenantFromPath('au')

    console.log('AU Sydney - Fetching with tenant:', tenant)

    // Try to get data, use fallbacks if not available
    const [addons, plans] = await Promise.all([
      getAddons(true, String(tenant.id)),
      getPlans(true, String(tenant.id)),
    ])

    // Get reviews and FAQ - these might fail for AU
    let reviewsData = null
    let faqListData
    try {
      reviewsData = await getReviews()
    } catch (e) {
      // Hide the reviews section rather than show made-up numbers
      console.log('Reviews API failed, hiding reviews')
    }

    try {
      faqListData = await getFAQList(
        client,
        context.locale || 'en-gb',
        'au-virtual-office-sydney',
      )
    } catch (e) {
      console.log('FAQ not found, using empty list')
      faqListData = { data: { slices: [], subtitle: '', title: 'FAQs' } }
    }

    console.log('AU Sydney - Plans returned:', plans?.length, 'plans')
    console.log('AU Sydney - Addons returned:', addons?.length, 'addons')

    return {
      props: {
        addons: addons || [],
        faqList: faqListData || [],
        plans: plans || [],
        reviews: reviewsData,
      },
    }
  } catch (e) {
    console.error('Error in getStaticProps:', e)
    return {
      props: {
        addons: [],
        faqList: [],
        plans: [],
        reviews: null,
      },
    }
  }
}
